import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Box,
  Typography,
  Container,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Snackbar,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { SeverErrorIllustration } from '../../../assets/pictures';

const RootStyle = styled(Stack)(({ theme }) => ({
  minHeight: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(10),
  backgroundColor: theme.palette.background.default,
  flexGrow: 1
}));

const Error500Page = ({ error, resetErrorBoundary } = {}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopyError = () => {
    if (error) {
      const errorDetails = `Erreur: ${error.toString()}\n\nStack Trace:\n${error.stack || 'Non disponible'}`;
      navigator.clipboard
        .writeText(errorDetails)
        .then(() => {
          setSnackbarOpen(true);
        })
        .catch((err) => {
          console.error('Erreur lors de la copie:', err);
        });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <RootStyle>
      <Container>
        <Box sx={{ maxWidth: 480, margin: 'auto', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            Désolé !
          </Typography>
          <Typography sx={{ color: 'text.secondary', mx: { xs: 5, sm: 10 } }}>
            Une erreur s'est produite, nous avons été prévenus, veuillez réessayer plus tard.
          </Typography>

          <SeverErrorIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <AlertTitle sx={{ m: 0, flexGrow: 1 }}>Détails de l'erreur</AlertTitle>
                <IconButton
                  size="small"
                  onClick={handleCopyError}
                  title="Copier les détails de l'erreur"
                  sx={{ mt: -0.5 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1 }}>
                {error.toString()}
              </Typography>

              {error.stack && (
                <>
                  <Button
                    size="small"
                    onClick={() => setShowDetails(!showDetails)}
                    sx={{ mt: 1 }}
                    startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {showDetails ? 'Masquer' : 'Afficher'} la stack trace
                  </Button>

                  <Collapse in={showDetails}>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 300,
                        overflow: 'auto',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                      }}
                    >
                      {error.stack}
                    </Typography>
                  </Collapse>
                </>
              )}
            </Alert>
          )}

          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="contained"
              onClick={() => {
                window.location.pathname = window.env?.PUBLIC_URL || '/';
              }}
            >
              Retour
            </Button>

            {resetErrorBoundary && (
              <Button variant="outlined" onClick={resetErrorBoundary}>
                Réessayer
              </Button>
            )}
          </Stack>
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Détails de l'erreur copiés dans le presse-papiers"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </RootStyle>
  );
};

Error500Page.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
    stack: PropTypes.string,
    toString: PropTypes.func
  }),
  resetErrorBoundary: PropTypes.func
};

Error500Page.defaultProps = {
  error: null,
  resetErrorBoundary: null
};

export default Error500Page;
